export interface ConsensusStopPoint {
  readonly time: number;
  readonly value: number;
}

export interface ConsensusStopSeries {
  readonly line: ReadonlyArray<ConsensusStopPoint>;
}

export interface ConsensusStopResult<T extends ConsensusStopSeries> {
  readonly chartData: Record<number, T>;
  readonly stopTimestamp?: number;
  readonly outcomeIndex?: number;
  readonly terminalValues?: Record<number, number>;
}

interface Crossing {
  readonly outcomeIndex: number;
  readonly time: number;
}

function isFiniteNumber(value: number): boolean {
  return Number.isFinite(value);
}

function isFinitePoint(point: ConsensusStopPoint | undefined): point is ConsensusStopPoint {
  return (
    point !== undefined &&
    isFiniteNumber(point.time) &&
    isFiniteNumber(point.value) &&
    point.time >= 0
  );
}

function interpolateTime(
  previous: ConsensusStopPoint,
  current: ConsensusStopPoint,
  threshold: number,
): number {
  const dt = current.time - previous.time;
  const dv = current.value - previous.value;
  if (dt <= 0 || dv === 0) return current.time;
  const ratio = (threshold - previous.value) / dv;
  return Math.round(previous.time + dt * Math.max(0, Math.min(1, ratio)));
}

function interpolateValue(
  previous: ConsensusStopPoint,
  current: ConsensusStopPoint,
  time: number,
): number {
  const dt = current.time - previous.time;
  if (dt <= 0) return current.value;
  const ratio = (time - previous.time) / dt;
  return previous.value + (current.value - previous.value) * Math.max(0, Math.min(1, ratio));
}

function findLineCrossing(
  line: ReadonlyArray<ConsensusStopPoint>,
  threshold: number,
): number | null {
  let previous: ConsensusStopPoint | undefined;
  for (const rawPoint of line) {
    if (!isFinitePoint(rawPoint)) continue;
    if (previous === undefined) {
      if (rawPoint.value >= threshold) return rawPoint.time;
      previous = rawPoint;
      continue;
    }
    if (previous.value < threshold && rawPoint.value >= threshold) {
      return interpolateTime(previous, rawPoint, threshold);
    }
    previous = rawPoint;
  }
  return null;
}

function findCrossing<T extends ConsensusStopSeries>(
  chartData: Readonly<Record<number, T | undefined>>,
  threshold: number,
  preferredOutcomeIndex: number | undefined,
): Crossing | null {
  if (!isFiniteNumber(threshold) || threshold <= 0) return null;

  if (preferredOutcomeIndex !== undefined) {
    const preferred = chartData[preferredOutcomeIndex];
    const preferredTime =
      preferred !== undefined ? findLineCrossing(preferred.line, threshold) : null;
    if (preferredTime !== null) {
      return { outcomeIndex: preferredOutcomeIndex, time: preferredTime };
    }
    return null;
  }

  let earliest: Crossing | null = null;
  for (const [key, entry] of Object.entries(chartData)) {
    if (entry === undefined) continue;
    const outcomeIndex = Number(key);
    if (!Number.isInteger(outcomeIndex)) continue;
    const time = findLineCrossing(entry.line, threshold);
    if (time === null) continue;
    if (
      earliest === null ||
      time < earliest.time ||
      (time === earliest.time && outcomeIndex < earliest.outcomeIndex)
    ) {
      earliest = { outcomeIndex, time };
    }
  }
  return earliest;
}

function valueAtTime(line: ReadonlyArray<ConsensusStopPoint>, time: number): number | undefined {
  let previous: ConsensusStopPoint | undefined;
  for (const rawPoint of line) {
    if (!isFinitePoint(rawPoint)) continue;
    if (rawPoint.time === time) return rawPoint.value;
    if (rawPoint.time > time) {
      return previous !== undefined ? interpolateValue(previous, rawPoint, time) : rawPoint.value;
    }
    previous = rawPoint;
  }
  return previous?.value;
}

function truncateLineAtTime(
  line: ReadonlyArray<ConsensusStopPoint>,
  stopTime: number,
  forcedTerminalValue: number | undefined,
): ReadonlyArray<ConsensusStopPoint> {
  if (line.length === 0) return line;

  const terminal = forcedTerminalValue ?? valueAtTime(line, stopTime);
  if (terminal === undefined || !isFiniteNumber(terminal)) return line;

  const next: ConsensusStopPoint[] = [];
  for (const rawPoint of line) {
    if (!isFinitePoint(rawPoint)) continue;
    if (rawPoint.time < stopTime) {
      next.push(rawPoint);
      continue;
    }
    if (rawPoint.time === stopTime) {
      next.push({ time: stopTime, value: terminal });
    }
    break;
  }

  const last = next[next.length - 1];
  if (last === undefined || last.time !== stopTime) {
    next.push({ time: stopTime, value: terminal });
  }
  return next;
}

export function stopConsensusChartAtThreshold<T extends ConsensusStopSeries>(args: {
  readonly chartData: Readonly<Record<number, T | undefined>>;
  readonly threshold: number | undefined;
  readonly enabled: boolean;
  readonly preferredOutcomeIndex?: number;
}): ConsensusStopResult<T> {
  const compactData: Record<number, T> = {};
  for (const [key, entry] of Object.entries(args.chartData)) {
    if (entry === undefined) continue;
    const outcomeIndex = Number(key);
    if (!Number.isInteger(outcomeIndex)) continue;
    compactData[outcomeIndex] = entry;
  }

  if (!args.enabled || args.threshold === undefined) return { chartData: compactData };

  const crossing = findCrossing(compactData, args.threshold, args.preferredOutcomeIndex);
  if (crossing === null) return { chartData: compactData };

  const chartData: Record<number, T> = {};
  const terminalValues: Record<number, number> = {};
  for (const [key, entry] of Object.entries(compactData)) {
    const outcomeIndex = Number(key);
    if (!Number.isInteger(outcomeIndex)) continue;
    const forced = outcomeIndex === crossing.outcomeIndex ? args.threshold : undefined;
    const line = truncateLineAtTime(entry.line, crossing.time, forced);
    const terminal = line[line.length - 1]?.value;
    if (terminal !== undefined && isFiniteNumber(terminal)) terminalValues[outcomeIndex] = terminal;
    chartData[outcomeIndex] = { ...entry, line } as T;
  }

  return {
    chartData,
    stopTimestamp: crossing.time,
    outcomeIndex: crossing.outcomeIndex,
    terminalValues,
  };
}
